'use client';
import { useSystemLabels } from '@eliadmin/core/src/hooks/use-system-labels';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import type { CartItemSegment } from '@eliadmin/core/src/components/cart/detail/cart-item';

import { NotInStockDialog } from '@eliadmin/core/src/components/product/dialog/not-in-stock-dialog';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useDialogStore } from '@eliadmin/core/src/components/ui/dialog';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useCartQueryOpts } from '@eliadmin/core/src/hooks/cart/use-cart';
import { useCartActions } from '@eliadmin/core/src/hooks/cart/use-cart-actions';
import { useDebouncedFn } from '@eliadmin/core/src/hooks/use-debounce';
import { useDisclosure } from '@eliadmin/core/src/hooks/use-disclosure';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { formatPrice } from '@eliadmin/core/src/utils/price-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useQuery } from '@tanstack/react-query';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import { usePathname } from 'next/navigation';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { FiMinus, FiPlus } from 'react-icons/fi';
import { PiShoppingCart } from 'react-icons/pi';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from '@eliadmin/core/src/components/ui/sheet';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { LuX } from 'react-icons/lu';
import { Separator } from '@eliadmin/core/src/components/ui/separator';

import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

export type HeaderCartButtonProps = {
  className?: { root?: string; info?: string; title?: string; price?: string; badge?: string };
  /** Media query that must match for this instance to auto-open the sheet on cart changes.
   *  Used to prevent double-open when the component is rendered for both desktop and mobile. */
  autoOpenMediaQuery?: string;
  /** Side the sheet slides in from. Defaults to 'right'. */
  side?: 'right' | 'bottom';
  /** Visual style of the trigger. 'icon' is the standalone header button (default),
   *  'bottomBar' renders it styled like a BottomBarItem (icon + label stacked). */
  trigger?: 'icon' | 'bottomBar';
};

export function HeaderCartButton({
  className,
  autoOpenMediaQuery,
  side = 'right',
  trigger = 'icon',
}: HeaderCartButtonProps) {
  const systemLabels = useSystemLabels();
  const templateSettings = useGlobalStore((state) => state.templateSettings);
  const currency = useGlobalStore((state) => state.generalLayout.currency);
  const currencySettings = useGlobalStore((state) => state.generalLayout.currency_settings);
  const { t } = useTranslations();

  const pathname = usePathname();
  const { isOpen, setIsOpen } = useDisclosure();
  const { data: cart } = useQuery({ ...useCartQueryOpts() });
  const scrollYRef = useRef(0);
  const wasOpenRef = useRef(false);

  // Save scroll position synchronously before the body scroll lock takes effect
  useLayoutEffect(() => {
    if (isOpen) {
      scrollYRef.current = window.scrollY;
    }
  }, [isOpen]);

  // Restore scroll when sheet closes — watch for when react-remove-scroll-bar
  // releases the body lock (removes data-scroll-locked) and restore at that exact moment
  useEffect(() => {
    if (isOpen) {
      wasOpenRef.current = true;
      return;
    }
    if (!wasOpenRef.current) return;
    wasOpenRef.current = false;

    const restore = () => {
      window.scrollTo({ top: scrollYRef.current, behavior: 'instant' });
    };

    restore();

    const observer = new MutationObserver(() => {
      if (!document.body.hasAttribute('data-scroll-locked')) {
        restore();
        observer.disconnect();
      }
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['data-scroll-locked'] });

    // Fallback after the 300ms close animation
    const timer = setTimeout(() => {
      restore();
      observer.disconnect();
    }, 350);

    return () => {
      observer.disconnect();
      clearTimeout(timer);
    };
  }, [isOpen]);

  // Prevent opening on first load
  const isFirstLoad = useRef(true);

  // Track previous quantities
  const prevQtyRef = useRef<Record<string, number>>({});

  useEffect(() => {
    if (!cart?.items) return;

    const currentQtyMap: Record<string, number> = {};
    cart.items.forEach((item) => (currentQtyMap[item.hash] = item.qty));

    if (isFirstLoad.current) {
      // Initial load: just store quantities
      prevQtyRef.current = currentQtyMap;
      isFirstLoad.current = false;
      return;
    }

    // Don't auto-open the sheet when already on the cart page
    if (pathname === ROUTES.CART()) {
      prevQtyRef.current = currentQtyMap;
      return;
    }

    // Detect any quantity change
    const quantityChanged = cart.items.some((item) => prevQtyRef.current[item.hash] !== item.qty);

    // Detect newly added items
    const addedItems = cart.items.some((item) => prevQtyRef.current[item.hash] === undefined);

    const mediaQueryMatches = !autoOpenMediaQuery || window.matchMedia(autoOpenMediaQuery).matches;

    if ((quantityChanged || addedItems) && mediaQueryMatches) {
      setIsOpen(true);
    }

    prevQtyRef.current = currentQtyMap;
  }, [cart?.items, setIsOpen, pathname, autoOpenMediaQuery]);

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen} defaultOpen={false}>
      {trigger === 'bottomBar' ? (
        <SheetTrigger asChild>
          <button aria-label={systemLabels('cart')}
            type="button"
            className={cn(
              'flex flex-col items-center space-y-1',
              'ea-bottom-bar__item text-muted-foreground',
              isOpen && 'ea-bottom-bar__item--active text-primary',
            )}
          >
            <div className="flex h-6 w-6 flex-col items-center justify-center ea-bottom-bar__item__icon-container">
              <PiShoppingCart className="h-5 w-5" />
            </div>
            <div className="text-center text-xs ea-bottom-bar__item__label">{t('text343')}</div>
          </button>
        </SheetTrigger>
      ) : (
        <div className="group relative shrink-0">
          <SheetTrigger asChild>
            <Button
              variant="default-alt"
              className={cn(
                'ea-cart-button shrink-0 gap-2 border-primary text-primary max-sm:h-12 max-sm:w-12 max-sm:!border-none max-sm:p-0',
                className?.root,
              )}
            >
              <div className="relative flex h-5 w-5 shrink-0 items-center gap-1">
                <span className="sr-only">{systemLabels('cart')} </span>
                <PiShoppingCart className="!group-hover:text-white max-h-6 min-h-6 min-w-6 max-w-6 sm:max-h-5 sm:min-h-5 sm:min-w-5 sm:max-w-5" />
              </div>

              {!!templateSettings.header?.cart_text?.enabled && (
                <div className={cn('hidden sm:grid', 'ea-cart-button__info', className?.info)}>
                  <p
                    className={cn(
                      'leading-2 min-w-[5.5rem] whitespace-nowrap text-end text-base font-medium tabular-nums text-primary',
                      'ea-cart-button__price',
                      className?.price,
                    )}
                  >
                    {formatPrice(cart?.items_total ?? 0, currency, currencySettings)}
                  </p>
                </div>
              )}
            </Button>
          </SheetTrigger>
        </div>
      )}

      <SheetContent
        side={side}
        closeIcon={{ wrapper: 'hidden' }}
        className={cn(
          'flex w-full flex-col bg-white p-0',
          side === 'bottom' ? 'max-h-[90dvh]' : 'h-screen max-w-[400px] sm:max-w-[600px]',
        )}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <VisuallyHidden.Root>
          <SheetTitle>{t('text163')}</SheetTitle>
        </VisuallyHidden.Root>
        <div className="flex w-full items-center justify-between px-6 pt-4">
          <p className="text-2xl font-bold text-black sm:text-3xl">{t('text163')}</p>
          <SheetClose>
            <LuX size={24} />
          </SheetClose>
        </div>
        <Separator className="bg-black/10" />
        <div className="flex-1 overflow-y-auto">
          {!cart?.items?.length ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 px-6 py-16 text-center">
              <PiShoppingCart size={44} className="text-black/20" />
              <p className="text-sm text-black/40">{t('text163')}</p>
            </div>
          ) : (
            <div className="divide-black/8 divide-y">
              {cart.items.map((item) => (
                <div key={item.hash} className="flex items-start gap-3 px-6 py-4">
                  <Image
                    src={item?.image?.url ?? '/images/placeholder.png'}
                    alt={resolveImageAlt(item?.image, item?.name)}
                    width={0}
                    height={0}
                    className="h-16 w-16 shrink-0 rounded border border-black/10 object-contain"
                    sizes="64px"
                  />
                  <div className="flex min-w-0 flex-1 flex-col gap-2">
                    <div className="flex items-start justify-between gap-2">
                      <p className="line-clamp-2 text-sm font-semibold leading-snug text-black">
                        {item.name}
                      </p>
                      <DeleteOrder item={item} />
                    </div>
                    <div className="flex items-center justify-between">
                      <Quantity item={item} />
                      <p className="text-sm font-bold text-black">
                        {formatPrice(item.qty * item.price, currency, currencySettings)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="w-full border-t border-black/10 px-6 pb-8 pt-4">
          <div className="mb-4 flex flex-col gap-2">
            {!!cart?.paymentType && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-black/80">{`${t('text158')}: ${cart.paymentType.name}`}</span>
                <span className="text-sm font-semibold text-black">
                  {cart.payment_type_total
                    ? formatPrice(cart.payment_type_total, currency, currencySettings, true)
                    : t('text349')}
                </span>
              </div>
            )}
            {!!cart?.shipment && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-black/80">{`${t('text153')}: ${cart.shipment.name}`}</span>
                <span className="text-sm font-semibold text-black">
                  {cart.shipment_total
                    ? formatPrice(cart.shipment_total, currency, currencySettings, true)
                    : t('text349')}
                </span>
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className="font-heading text-2xl font-bold text-black">{t('text165')}</span>
              <span className="font-heading text-2xl font-bold text-black">
                {formatPrice(cart?.total || 0, currency, currencySettings)}
              </span>
            </div>
          </div>
          <SheetClose asChild>
            <Button asChild variant={'dark'} className="w-full">
              <Link prefetch={false} href={ROUTES.CART()}>
                {t('text456')}
              </Link>
            </Button>
          </SheetClose>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Quantity({ item, className }: CartItemSegment) {
  const { updateQuantityMutation } = useCartActions();
  const openModal = useDialogStore((s) => s.open);

  const [internalValue, setInternalValue] = useState<string>(String(item.qty));
  const debouncedMutate = useDebouncedFn(updateQuantityMutation.mutate, 1000);

  const isSellable = item.availability?.sellable;

  const openNotInStockDialog = () => {
    openModal({
      id: NotInStockDialog.id,
      content: <NotInStockDialog maxQuantity={item.stock} unit={item.unit} />,
    });
  };

  useEffect(() => {
    setInternalValue(String(item.qty));
  }, [item.qty]);

  const disabled = item.delivery_by_date;

  return (
    <div
      className={cn(
        'flex items-center overflow-hidden rounded-full border border-black/15',
        disabled && 'pointer-events-none opacity-40',
        className,
      )}
    >
      <button
        className="flex h-7 w-7 items-center justify-center text-black/50 transition-colors hover:bg-black/5 hover:text-black disabled:opacity-30"
        disabled={item.qty <= 1}
        onClick={(e) => {
          e.stopPropagation();
          const internalNumValue = Number(internalValue);
          if (!isNaN(internalNumValue) && internalNumValue > 1) {
            setInternalValue(String(internalNumValue - 1));
            debouncedMutate({ hash: item.hash, qty: internalNumValue - 1 });
          }
          if (internalNumValue <= 1) setInternalValue('1');
        }}
      >
        <FiMinus size={12} />
      </button>
      <input
        className="w-8 border-x border-black/15 bg-transparent py-0 text-center text-sm font-medium text-black outline-none"
        value={internalValue}
        onChange={(e) => {
          const strValue = e.target.value;
          setInternalValue(strValue);
          if (!strValue) return;
          const numValue = Number(strValue);
          if (isNaN(numValue)) return;
          if (numValue >= 1 && (numValue <= item.stock || isSellable)) {
            debouncedMutate({ hash: item.hash, qty: numValue });
          }
        }}
        onBlur={() => {
          const numValue = Number(internalValue);
          if (isNaN(numValue)) {
            setInternalValue(String(item.qty));
          }
          if (numValue < 1) {
            setInternalValue('1');
          }
          if (numValue > item.stock && !isSellable) {
            openNotInStockDialog();
            setInternalValue(String(item.stock));
          }
        }}
      />
      <button
        className="flex h-7 w-7 items-center justify-center text-black/50 transition-colors hover:bg-black/5 hover:text-black"
        onClick={(e) => {
          e.stopPropagation();
          const internalNumValue = Number(internalValue);
          if (internalNumValue >= item.stock && !isSellable) {
            return openNotInStockDialog();
          }
          if (!isNaN(internalNumValue) && (internalNumValue < item.stock || isSellable)) {
            setInternalValue(String(internalNumValue + 1));
            debouncedMutate({ hash: item.hash, qty: internalNumValue + 1 });
          }
        }}
      >
        <FiPlus size={12} />
      </button>
    </div>
  );
}

function DeleteOrder(props: CartItemSegment) {
  const { deleteCartMutation } = useCartActions();

  function handleDelete() {
    deleteCartMutation.mutate({ hash: props.item.hash });

    if (window.dataLayer) {
      window.dataLayer.push({ ec: null });
      window.dataLayer.push({
        event: 'removeFromCart',
        ec: {
          currencyCode: useGlobalStore.getState().generalLayout.currency,
          remove: {
            products: [
              {
                id: props.item.id,
                name: props.item.name,
                price: props.item.price,
                quantity: props.item.qty,
              },
            ],
          },
        },
      });
    }
  }

  return (
    <button
      onClick={handleDelete}
      className={cn(
        'shrink-0 rounded p-0.5 text-black/25 transition-colors hover:text-red-500',
        props.className,
      )}
    >
      {deleteCartMutation.isPending ? <Spinner className="h-4 w-4" /> : <LuX size={15} />}
    </button>
  );
}
